/**
 * DetailsPane — the FULL selection inspector, bound to the workspace.
 *
 * Renders the shared `Sidebar` (module/aspect/interface/edge details, the
 * process tree + scrubber, diagnostics, backlinks, space overview) entirely
 * from the workspace services:
 *
 *   - store        — selection, blast radius, drill-down history (back nav),
 *     focal path; drill-style picks dispatch `applySelection("sidebar")` so
 *     the back button can undo them, and module picks emit `boardFly`.
 *   - model ctx    — the shared compile (model, edges, diagnostics,
 *     backlinks, blast-radius query).
 *   - playback ctx — the process walk, scrubber state and process actions
 *     (requires a `ProcessPlaybackProvider` in the composition).
 *
 * Board-independent by default: it builds its own space tree from the
 * model. A host that already pays for a solved layout can hand in its
 * `root`/`boardIndexes` (the viewer does — its layout-context indexes also
 * cover spaces the lazy board hasn't solved yet).
 *
 * `onArchlangNav` is the one injection point: description links can target
 * surfaces only some hosts have (documents, mode switches). The default
 * routes module/process/interface/aspect picks through the store and
 * ignores document links.
 */
import { useCallback, useMemo, type ReactElement, type ReactNode } from "react";
import type { Diagnostic, StableId } from "@archlang/scene/arch-model";
import { flattenModulesWithParent } from "@archlang/scene/arch-model";
import { buildBoardFromModules, asSource, type SpaceLike } from "@archlang/scene/board";
import { Sidebar, type BoardIndexes, type SidebarTab } from "../../components/Sidebar.js";
import { findBoardEdge } from "../../architecture-view/findBoardEdge.js";
import {
  buildBoardIndexes as buildBoardIndexesFromRoot,
  buildModelIndexes as buildViewIndexes,
} from "../../architecture-view/view-indexes.js";
import {
  resolveArchlangNav,
  resolveSelectedAspect,
  resolveSelectedInterface,
} from "../../derivations.js";
import { useModelIndexes } from "../../hooks/useModelIndexes.js";
import { useWidgetDiagnostics } from "../../hooks/useWidgetDiagnostics.js";
import { useWorkspace } from "../WorkspaceContext.js";
import {
  useBlastRadius,
  useFocalPath,
  useNavHistory,
  useWorkspaceSelection,
} from "../WorkspaceStore.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import { useModuleEditing } from "../useModuleEditing.js";
import { useLensEntries } from "../useLensEntries.js";
import { useProcessPlaybackContext } from "../ProcessPlaybackContext.js";
import {
  usePluginRegistry,
  resolveDetailSections,
} from "../plugins/PluginRegistryContext.js";
import { PluginBoundary } from "../plugins/WorkbenchPluginHost.js";
import type { DetailSelection } from "../plugins/types.js";

export interface DetailsPaneProps {
  /** Reuse the host's solved space tree instead of building one from the
   *  model (the viewer passes its layout root). */
  readonly root?: SpaceLike | null;
  /** Reuse the host's connection indexes (the viewer's layout-context build
   *  also covers spaces the lazy board hasn't solved yet). */
  readonly boardIndexes?: BoardIndexes;
  /** Extra diagnostics merged ahead of the pane's own (rarely needed —
   *  the pane already folds in widget-registration warnings). */
  readonly diagnostics?: readonly Diagnostic[];
  /** Route an `archlang://` description link. Omit for the store default
   *  (module/process/interface/aspect; document links are host surfaces). */
  readonly onArchlangNav?: (href: string) => void;
  /** Enable inline editing of the selected module (W1, WYSIWYG plan §7).
   *  The host passes its editability condition (the same one gating the
   *  editor); absent/false = today's read-only pane. */
  readonly editable?: boolean;
}

export function DetailsPane(props: DetailsPaneProps): ReactElement | null {
  const store = useWorkspace();
  const selection = useWorkspaceSelection(store);
  const blastRadius = useBlastRadius(store);
  const navHistory = useNavHistory(store);
  const focalPath = useFocalPath(store);
  const { model, edges, diagnostics, backlinks, blastRadiusIds } = useWorkspaceModel();
  const playback = useProcessPlaybackContext();

  const indexes = useModelIndexes(model);
  const allDiagnostics = useWidgetDiagnostics(props.diagnostics ?? diagnostics);

  // Space tree: the host's solved layout when given, else built from the
  // model (positions are irrelevant to the inspector).
  const ownRoot = useMemo(() => {
    if (props.root || !model || model.modules.length === 0) return null;
    const flat = flattenModulesWithParent(model.modules);
    return buildBoardFromModules(flat, edges ?? [], new Map(), new Map(), false);
  }, [props.root, model, edges]);
  const root = props.root ?? ownRoot;
  // Fully expand once — the inspector indexes nested entities; the board
  // itself renders lazily.
  const fullRoot = useMemo(
    () => (root ? asSource(root).at([], Infinity) : null),
    [root],
  );

  const boardIndexes = useMemo(
    () =>
      props.boardIndexes ??
      buildBoardIndexesFromRoot(fullRoot, model ? buildViewIndexes(model) : null),
    [props.boardIndexes, fullRoot, model],
  );

  const selectedModule = selection.moduleId
    ? indexes?.moduleById.get(selection.moduleId) ?? null
    : null;
  // Inline-editing bundle for the selected module — null unless the host
  // opted in AND a real (non-aspect) module is selected.
  const editing = useModuleEditing(
    props.editable && selectedModule ? selection.moduleId : null,
  );
  // Lens entries (VIEWS.md §8): top-level/space + module-scope declarations,
  // add/override by the active view — resolved off the shared compile.
  const lensEntries = useLensEntries(selectedModule ? selection.moduleId : null);
  const selectedAspect = useMemo(
    () => resolveSelectedAspect(selection.moduleId as StableId | null, model),
    [selection.moduleId, model],
  );
  const selectedInterface = useMemo(
    () => resolveSelectedInterface(selection.interfaceKey, indexes),
    [selection.interfaceKey, indexes],
  );
  const selectedEdge = useMemo(
    () => (selection.edgeId ? findBoardEdge(fullRoot, selection.edgeId) : null),
    [selection.edgeId, fullRoot],
  );

  // Plugin detail sections (OTLP telemetry, …). Resolve the current selection
  // to the plugin-facing DetailSelection (module/edge — the surfaces that host
  // a plugin section today; interface/aspect produce none yet), ask the registry
  // which contributions apply, and render each inside its own error boundary.
  // The resolved node is threaded DOWN as a prop so the Sidebar views stay pure.
  const registry = usePluginRegistry();
  const detailSelection = useMemo<DetailSelection | null>(() => {
    if (selectedEdge)
      return {
        kind: "edge",
        edgeId: selectedEdge.id,
        from: selectedEdge.from,
        to: selectedEdge.to,
      };
    if (selectedModule && selection.moduleId)
      return { kind: "module", moduleId: selection.moduleId };
    return null;
  }, [selectedEdge, selectedModule, selection.moduleId]);
  const pluginSections = useMemo<ReactNode>(() => {
    if (!detailSelection) return null;
    const owned = resolveDetailSections(registry, detailSelection);
    if (owned.length === 0) return null;
    return owned.map(({ pluginId, contribution }) => (
      <PluginBoundary key={`${pluginId}:${contribution.id}`} pluginId={pluginId}>
        <contribution.Section selection={detailSelection} />
      </PluginBoundary>
    ));
  }, [registry, detailSelection]);

  // Sidebar drill-down: select AND fly, pushing the back-stack.
  const handleNavigateModule = useCallback(
    (id: StableId) => {
      store.applySelection({ moduleId: id, edgeId: null }, "sidebar");
      store.emit({ type: "boardFly", moduleId: id });
    },
    [store],
  );
  const handleNavigateEdge = useCallback(
    (edgeId: string | null) => {
      store.applySelection({ moduleId: null, edgeId }, "sidebar");
    },
    [store],
  );
  // Select an interface by qualified name: also select the owning module so
  // the board keeps context; the inspector prioritises the interface.
  const handleSelectInterface = useCallback(
    (qualified: string) => {
      const owner = indexes?.interfaceById.get(qualified);
      if (owner) {
        store.applySelection({ moduleId: owner.moduleId, edgeId: null }, "sidebar");
      }
      store.select({ interfaceKey: qualified });
    },
    [store, indexes],
  );
  const handleBlastRadius = useCallback(() => {
    const id = store.getState().selection.moduleId;
    if (id) store.setBlastRadius(blastRadiusIds(id as StableId));
  }, [store, blastRadiusIds]);

  const { onArchlangNav } = props;
  const handleArchlangNav = useCallback(
    (href: string) => {
      if (onArchlangNav) return onArchlangNav(href);
      const action = resolveArchlangNav(href, model, indexes);
      if (!action) return;
      if (action.kind === "process") playback.handleActivateProcess(action.processId);
      else if (action.kind === "module") handleNavigateModule(action.moduleKey);
      else if (action.kind === "interface") handleSelectInterface(action.interfaceId);
      else if (action.kind === "type") {
        // Same landing a ⌘F / nav-tree pick gives a declared type (r53).
        store.openType(action.name);
      } else if (action.kind === "aspect") {
        if (action.value !== undefined) {
          store.applySelection(
            {
              moduleId: `aspect:${action.key}=${JSON.stringify(action.value)}` as StableId,
              edgeId: null,
            },
            "sidebar",
          );
        } else {
          store.setAspectColorKey(action.key);
        }
      }
      // "document" — a host surface (the viewer routes it via onArchlangNav).
    },
    [onArchlangNav, model, indexes, playback, handleNavigateModule, handleSelectInterface, store],
  );

  if (!model || !fullRoot) return null;

  return (
    <Sidebar
      selectedModule={selectedModule}
      selectedAspect={selectedAspect}
      selectedEdge={selectedEdge}
      selectedInterface={selectedInterface}
      indexes={indexes}
      boardIndexes={boardIndexes}
      backlinks={backlinks}
      root={fullRoot}
      focalPath={focalPath}
      processes={model.processes}
      activeProcessId={playback.activeProcessId}
      diagnostics={allDiagnostics}
      onBlastRadius={handleBlastRadius}
      blastRadius={blastRadius}
      activeProcess={
        playback.activeProcessId
          ? indexes?.processById.get(playback.activeProcessId) ?? null
          : null
      }
      onSelectEdge={handleNavigateEdge}
      onSelectInterface={handleSelectInterface}
      onSelectModule={handleNavigateModule}
      onSelectProcess={playback.handleSelectProcess}
      onArchlangNav={handleArchlangNav}
      subprocessDescriptions={playback.subprocessDescriptions}
      onActivateProcess={playback.handleActivateProcess}
      onBack={() => store.goBack()}
      canGoBack={navHistory.length > 0}
      historyPast={navHistory.map((e) => ({
        moduleId: e.selection.moduleId as StableId | null,
        edgeId: e.selection.edgeId,
      }))}
      activeTab={"details" as SidebarTab}
      walkResult={playback.walkResult}
      activeStepIndex={playback.activeStepIndex}
      walkerState={playback.activeWalkerState}
      onBranchSelect={playback.selectBranch}
      onSubprocessToggle={playback.toggleSubprocess}
      onSelectStepIndex={playback.setActiveStepIndex}
      editing={editing}
      lensEntries={lensEntries}
      pluginSections={pluginSections}
    />
  );
}
